import { useEffect } from "react";
import { Link } from "react-router";
import { css, styled } from "styled-components";

import { RoundedButton, SectionTitleUnderlined } from "common/components";
import { ChevronIcon } from "common/components/icons";
import { breakpoints, linkStyles, mediaStyles } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { StyledLandingCard } from "domains/landing/components/StyledLandingCard";
import { getSiteLanguage } from "domains/language/selectors";
import { TopicSlider } from "domains/topic/components/TopicSlider";
import { getCanFollowTopic } from "entities/permissions/selectors";
import { fetchFollowedTopics } from "entities/topics/actions";
import { createTopicsSelector, getFollowedTopicsIds, getFollowedTopicsIsPending } from "entities/topics/selectors";

const StyledContainer = styled.div`
  ${mediaStyles(({ spacing }) => `margin: 1rem ${spacing}rem;`)};
`;

const StyledTitle = styled(SectionTitleUnderlined)`
  ${mediaStyles(({ spacing }) => `margin: 2rem ${spacing}rem 1rem;`)};
`;

const StyledPlaceholderCard = styled(StyledLandingCard)`
  display: flex;
  align-items: center;
  flex-direction: column;

  @media ${breakpoints.medium} {
    flex-direction: row;
  }
`;

const StyledPlaceholderText = styled.p`
  margin: 1rem 0;

  @media ${breakpoints.medium} {
    margin: 0 2rem;
  }
`;

const StyledButton = styled(RoundedButton)`
  color: white;
  ${({ theme }) => css`
    background-color: ${theme.colors.blue};

    &:hover {
      background-color: ${theme.colors.blueDark};
    }
  `};
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const StyledArrow = styled(ChevronIcon)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.textBright};
  padding-left: 0.5rem;
`;

export const FollowingTopicsSection = () => {
  const dispatch = useDispatch();
  const followingTopics = useMemoisedSelector(getFollowedTopicsIds, createTopicsSelector);
  const canFollowTopic = useSelector(getCanFollowTopic);
  const isPending = useSelector(getFollowedTopicsIsPending);
  const siteLanguage = useSelector(getSiteLanguage);
  const translate = useTranslate();

  useEffect(() => {
    if (canFollowTopic) {
      dispatch(fetchFollowedTopics({ offset: 0, limit: 8 }));
    }
  }, [canFollowTopic, dispatch, siteLanguage]);

  return canFollowTopic ? (
    <>
      <StyledTitle isLoading={isPending}>
        <StyledLink to="/followed">
          {translate("app.monitor.following_topics")}
          <StyledArrow />
        </StyledLink>
      </StyledTitle>
      {followingTopics.length === 0 && !isPending ? (
        <StyledContainer>
          <StyledPlaceholderCard>
            <StyledPlaceholderText>{translate("app.about.key_points.personalized_content.text")}</StyledPlaceholderText>
            <StyledButton to="/topics">{translate("app.discover_topics.title")}</StyledButton>
          </StyledPlaceholderCard>
        </StyledContainer>
      ) : (
        <StyledContainer>
          <TopicSlider topicBack={{ term: "app.home" }} topics={followingTopics.slice(0, 8)} />
        </StyledContainer>
      )}
    </>
  ) : null;
};
